import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUpdateMute } from '@connect-x-app/hybrid-state';
import { Switch, Text } from '@connect-x-app/hybrid-ui';
import { ApiError } from '@connect-x-app/shared-domain-models';
import { labelColors } from '@connect-x-app/shared-ui';
import { convertDurationStringToDuration } from '@connect-x-app/shared-utils';
import { FLIGHTROOM_TEST_IDS } from '@connect-x-app/web-test-ids';
import { Icon, ModalOpener, toastError } from '@connect-x-app/web-ui';
import { Temporal } from '@js-temporal/polyfill';

import styles from './Mute.module.scss';
import { MuteFlightroomModal } from './MuteFlightroomModal';

type Props = {
  className?: string;
  flightroomId: string;
  isDisabled?: boolean;
  isSelected?: boolean;
  muted: boolean;
  mutedUntilDurationBeforeDeparture: string | null;
};

export const Mute: FC<Props> = ({
  className = '',
  flightroomId,
  isDisabled,
  isSelected,
  muted,
  mutedUntilDurationBeforeDeparture,
}) => {
  const { t } = useTranslation();
  const [notificationsEnabled, setNotificationsEnabled] = useState(!muted);

  const { mutateAsync: muteFlightroom } = useUpdateMute();

  const muteDuration: Temporal.Duration | undefined = useMemo(
    () => (mutedUntilDurationBeforeDeparture ? convertDurationStringToDuration(mutedUntilDurationBeforeDeparture) : undefined),
    [mutedUntilDurationBeforeDeparture],
  );

  useEffect(() => {
    setNotificationsEnabled(!muted);
  }, [muted]);

  const handleChange = async (value: boolean) => {
    setNotificationsEnabled(value);
    if (!value) {
      ModalOpener.instance.open({
        render: () => <MuteFlightroomModal onDismiss={() => setNotificationsEnabled(true)} onSelect={onMuteFlightroom} />,
      });
    } else {
      await unmuteFlightroom();
    }
  };

  const unmuteFlightroom = async () => {
    try {
      const mute = false;
      const muteDuration = null;
      await muteFlightroom({ duration: muteDuration, muted: mute, roomId: flightroomId });
    } catch (error: unknown) {
      handleMuteError(error as ApiError);
      setNotificationsEnabled(false);
    }
  };

  const onMuteFlightroom = async (duration: string) => {
    try {
      const mute = true;
      const muteDuration = `PT${duration}H`;
      await muteFlightroom({ duration: muteDuration, muted: mute, roomId: flightroomId });
    } catch (error: unknown) {
      handleMuteError(error as ApiError);
      setNotificationsEnabled(true);
    }
  };

  const handleMuteError = (error: ApiError) => {
    if (error.error === 'InvalidDuration') {
      toastError(`${t('FLIGHTROOMS.MUTE.ERROR.NOT_APPLIED')} ${t('FLIGHTROOMS.MUTE.ERROR.INVALID_HOURS')}`);
    } else {
      toastError(t('FLIGHTROOMS.MUTE.ERROR.UNSUCCESSFUL'));
    }
  };

  return (
    <div className={`${styles['mute-container']} ${className}`}>
      <div className={styles['mute']}>
        <Icon
          colorName={isSelected ? labelColors['$label-light'] : labelColors['$label-primary']}
          name={!muted ? 'notificationBellOutline' : 'notificationBellSlashOutline'}
          size={2.4}
        />
        <div className={styles['mute__duration']}>
          <Text size="SM">{t(!muted ? 'FLIGHTROOMS.MUTE.NOTIFICATION_ON' : 'FLIGHTROOMS.MUTE.NOTIFICATION_OFF')}</Text>
          {muted && muteDuration ? (
            <Text as="span" size="XS">
              {t('FLIGHTROOMS.MUTE.UNTIL', { hours: muteDuration?.hours })}
            </Text>
          ) : null}
        </div>
      </div>
      <Switch
        className={styles['mute__switch']}
        isDisabled={isDisabled}
        onChange={handleChange}
        size={'s'}
        testId={FLIGHTROOM_TEST_IDS.mute.notifications}
        type={isSelected ? 'primary-white' : 'primary'}
        value={notificationsEnabled}
      />
    </div>
  );
};
